import React, { useState, useEffect, useCallback } from 'react';
import { Typography } from '@mui/material';
import UserAppBar from '../UserAppBar';
import ProfileShell from '../profile/ProfileShell';
import OverviewTab from '../UserDetail/OverviewTab';
import SecurityTab from './SecurityTab';
import { getCurrentUser } from '../../services/accountService';
import { SessionExpiredError } from '../../services/sessionExpiry';

const TABS = ['Overview', 'Security'];

/**
 * Adapt common.UserDTO onto the canonical profile the shared shell consumes.
 *
 * The two profile endpoints disagree on more than they agree: GET /user answers
 * `activeSubscription` and `subscriptionExpiration` in camelCase where
 * GET /admin/users/:id answers snake_case, and its `roles` is a plain string
 * array rather than the admin endpoint's records with grant provenance. The
 * canonical shape follows the admin spelling, so this is where the renaming
 * happens and UserDetail needs no adapter at all.
 *
 * `roles` is deliberately dropped: without provenance it cannot feed the
 * Permissions tab, and the account page does not show one.
 */
const toProfile = (user) => ({
  id: user.id,
  username: user.username,
  first_name: user.first_name,
  middle_name: user.middle_name,
  last_name: user.last_name,
  dob: user.dob,
  addresses: user.addresses || [],
  active_subscription: user.activeSubscription,
  subscription_expiration: user.subscriptionExpiration,
});

/**
 * The signed-in user's own account page.
 *
 * Shares its layout with the staff-facing UserDetail but keeps a separate data
 * path, because the responses are different shapes and one component serving
 * both would have to guess which it was looking at.
 *
 * No permission is required beyond being signed in — the route wraps this in a
 * bare ProtectedRoute. Everyone has an account; the tab that matters here acts
 * on the bearer token's own user.
 */
function Account() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setProfile(toProfile(await getCurrentUser()));
    } catch (err) {
      // A SessionExpiredError has already cleared the session and started the
      // redirect. Setting an error would flash a message on a page that is
      // leaving anyway.
      if (!(err instanceof SessionExpiredError)) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return (
    <ProfileShell
      profile={profile}
      loading={loading}
      error={error}
      tabs={TABS}
      idPrefix="account"
      tabsAriaLabel="Account sections"
      activeTab={activeTab}
      onTabChange={setActiveTab}
      // The user-facing bar, the same one the Dashboard this page is reached
      // from renders. The shared shell used to supply StaffAppBar itself, which
      // rebranded this page as the admin dashboard mid-click.
      appBar={<UserAppBar />}
      // A plain title, spelled exactly as the dashboards and System
      // Administration spell theirs. The breadcrumb this replaced led back to
      // /dashboard, but every app bar in the product offers My Account, so the
      // crumb usually named somewhere the reader had not been.
      header={
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          My Account
        </Typography>
      }
    >
      {/* GET /user carries no SSN and no activity counters, so those sections
          are switched off rather than rendering empty placeholders. */}
      {activeTab === 0 && (
        <OverviewTab detail={profile} showSsn={false} showActivity={false} />
      )}
      {activeTab === 1 && <SecurityTab />}
    </ProfileShell>
  );
}

export default Account;
